import * as THREE from 'three';

export type SignType =
  | 'Z205_VORFAHRT_GEWAEHREN'
  | 'Z206_STOPP'
  | 'Z215_KREISVERKEHR'
  | 'Z274_TEMPO_30'
  | 'Z274_TEMPO_50'
  | 'Z274_1_ZONE_30_BEGINN'
  | 'Z274_2_ZONE_30_ENDE'
  | 'Z306_VORFAHRTSSTRASSE'
  | 'Z307_ENDE_VORFAHRTSSTRASSE'
  | 'Z301_VORFAHRT_KREUZUNG'
  | 'Z293_ZEBRASTREIFEN'
  | 'Z350_FUSSGAENGERUEBERWEG'
  | 'Z134_FUSSGAENGER_WARNUNG'
  | 'Z136_ACHTUNG_KINDER'
  | 'Z101_GEFAHRSTELLE'
  | 'Z103_KURVE_LINKS'
  | 'Z103_KURVE_RECHTS'
  | 'Z105_DOPPELKURVE'
  | 'Z625_KURVENLEITTAFEL_LINKS'
  | 'Z625_KURVENLEITTAFEL_RECHTS'
  | 'Z276_UEBERHOLVERBOT'
  | 'Z224_HALTESTELLE'
  | 'Z357_SACKGASSE'
  | 'Z123_BAUSTELLE'
  | 'Z131_LICHTZEICHENANLAGE'
  | 'Z283_ABSOLUTES_HALTEVERBOT'
  | 'Z286_EINGESCHRAENKTES_HALTEVERBOT'
  | 'Z267_VERBOT_DER_EINFAHRT'
  | 'Z222_VORBEIFAHRT_RECHTS'
  | 'Z314_PARKEN'
  | 'Z325_VERKEHRSBERUHIGT'
  | 'Z310_ORTSTAFEL'
  | 'STRASSE_HAUPTSTRASSE'
  | 'STRASSE_GOETHESTRASSE'
  | 'STRASSE_BAHNHOFSTRASSE'
  | 'STRASSE_SCHULSTRASSE'
  | 'Z201_ANDREASKREUZ'
  | 'Z102_KREUZUNG_RVL'
  | 'Z156_BAKE_3_STREIFEN'
  | 'Z159_BAKE_2_STREIFEN'
  | 'Z162_BAKE_1_STREIFEN'
  | 'Z237_RADWEG'
  | 'Z138_RADVERKEHR'
  | 'Z220_EINBAHNSTRASSE'
  | 'Z244_FAHRRADSTRASSE'
  | 'Z245_ENDE_FAHRRADSTRASSE'
  | 'Z330_1_AUTOBAHN_BEGINN'
  | 'Z330_2_AUTOBAHN_ENDE'
  | 'Z311_ORTSTAFEL_ENDE'
  | 'Z282_AUFHEBUNG'
  | 'Z450_BAKE_300M'
  | 'Z450_BAKE_200M'
  | 'Z450_BAKE_100M'
  | 'Z315_PARKEN_GEHWEG';


export class TrafficSigns {
  public static createSign(type: SignType, position: THREE.Vector3, rotationY: number = 0): THREE.Group {
    const group = new THREE.Group();
    group.name = `sign_${type}`;
    group.userData = { signType: type, isSign: true };
    group.position.copy(position);
    group.rotation.y = rotationY;

    // Standard German Galvanized Steel Pole (2.2m clearance height)
    const poleGeom = new THREE.CylinderGeometry(0.035, 0.035, 2.4, 16);
    const poleMat = new THREE.MeshStandardMaterial({ color: 0x94a3b8, metalness: 0.8, roughness: 0.3 });
    const pole = new THREE.Mesh(poleGeom, poleMat);
    pole.position.y = 1.2;
    pole.castShadow = true;
    group.add(pole);

    const signPlate = TrafficSigns.createSignPlate(type);
    signPlate.name = `plate_${type}`;
    signPlate.userData = { signType: type };
    signPlate.position.set(0, 2.2, 0.04);
    group.add(signPlate);

    return group;
  }

  private static createSignPlate(type: SignType): THREE.Group {
    const plateGroup = new THREE.Group();
    const isStreetSign = (
      type === 'STRASSE_HAUPTSTRASSE' ||
      type === 'STRASSE_GOETHESTRASSE' ||
      type === 'STRASSE_BAHNHOFSTRASSE' ||
      type === 'STRASSE_SCHULSTRASSE' ||
      type === 'Z220_EINBAHNSTRASSE'
    );
    const isOrtstafel = type === 'Z310_ORTSTAFEL' || type === 'Z311_ORTSTAFEL_ENDE';
    const isAutobahnBake = (
      type === 'Z450_BAKE_300M' ||
      type === 'Z450_BAKE_200M' ||
      type === 'Z450_BAKE_100M'
    );

    const width = isStreetSign ? 0.86 : isOrtstafel ? 0.90 : isAutobahnBake ? 0.40 : 0.72;
    const height = isStreetSign ? 0.24 : isOrtstafel ? 0.58 : isAutobahnBake ? 0.80 : 0.72;
    const depth = 0.024;

    if (typeof document === 'undefined') {
      const basicMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
      const box = new THREE.Mesh(new THREE.BoxGeometry(width, height, depth), basicMat);
      plateGroup.add(box);
      return plateGroup;
    }

    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d')!;


    // Anti-aliased high res sign graphics
    ctx.fillStyle = '#ffffff';

    if (type === 'Z205_VORFAHRT_GEWAEHREN') {
      // Inverted Triangle (Vorfahrt gewähren)
      ctx.fillStyle = '#dc2626';
      ctx.beginPath();
      ctx.moveTo(256, 480);
      ctx.lineTo(32, 48);
      ctx.lineTo(480, 48);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.moveTo(256, 390);
      ctx.lineTo(96, 96);
      ctx.lineTo(416, 96);
      ctx.closePath();
      ctx.fill();
    } else if (type === 'Z206_STOPP') {
      // Octagonal STOP sign
      ctx.fillStyle = '#dc2626';
      ctx.beginPath();
      const r = 220;
      for (let i = 0; i < 8; i++) {
        const a = (i * Math.PI) / 4 + Math.PI / 8;
        const x = 256 + r * Math.cos(a);
        const y = 256 + r * Math.sin(a);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.fill();

      // White STOP text
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 116px Inter, Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('STOP', 256, 256);
    } else if (type === 'Z215_KREISVERKEHR') {
      // German StVO Zeichen 215: Blue Circle with 3 curved white arrows around the edge
      ctx.fillStyle = '#1d4ed8';
      ctx.beginPath();
      ctx.arc(256, 256, 230, 0, Math.PI * 2);
      ctx.fill();

      // 3 curved arrows around the edge (separated by 120°), rotating counter-clockwise
      const arrowRadius = 138;
      const arcSection = (Math.PI * 2) / 3;
      for (let i = 0; i < 3; i++) {
        // Counter-clockwise in screen coords means decreasing angle, or startAngle to endAngle
        const startAngle = i * arcSection - Math.PI / 2;
        const endAngle = startAngle - Math.PI * 0.40;

        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 26;
        ctx.lineCap = 'round';
        ctx.beginPath();
        // anticlockwise = true
        ctx.arc(256, 256, arrowRadius, startAngle, endAngle, true);
        ctx.stroke();

        // Arrow head at endAngle pointing counter-clockwise (tangent in direction of travel)
        const headX = 256 + Math.cos(endAngle) * arrowRadius;
        const headY = 256 + Math.sin(endAngle) * arrowRadius;
        // Direction tangent (counter-clockwise: angle decreasing)
        const tangentX = Math.sin(endAngle);
        const tangentY = -Math.cos(endAngle);
        const normalX = -tangentY;
        const normalY = tangentX;

        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.moveTo(headX + tangentX * 26, headY + tangentY * 26);
        ctx.lineTo(headX - tangentX * 10 + normalX * 22, headY - tangentY * 10 + normalY * 22);
        ctx.lineTo(headX - tangentX * 10 - normalX * 22, headY - tangentY * 10 - normalY * 22);
        ctx.closePath();
        ctx.fill();
      }
    } else if (type === 'Z274_TEMPO_30' || type === 'Z274_TEMPO_50') {
      // Red ring speed limit
      ctx.fillStyle = '#dc2626';
      ctx.beginPath();
      ctx.arc(256, 256, 230, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(256, 256, 176, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#0f172a';
      ctx.font = 'bold 190px Inter, Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(type === 'Z274_TEMPO_30' ? '30' : '50', 256, 268);
    } else if (type === 'Z274_1_ZONE_30_BEGINN') {
      // White square with "ZONE" and 30 circle
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(20, 20, 472, 472);
      ctx.lineWidth = 12;
      ctx.strokeStyle = '#0f172a';
      ctx.strokeRect(20, 20, 472, 472);

      ctx.fillStyle = '#0f172a';
      ctx.font = 'bold 54px Inter, Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('ZONE', 256, 95);

      // Red 30 circle inside
      ctx.fillStyle = '#dc2626';
      ctx.beginPath();
      ctx.arc(256, 280, 140, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(256, 280, 105, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#0f172a';
      ctx.font = 'bold 110px Inter, Arial, sans-serif';
      ctx.textBaseline = 'middle';
      ctx.fillText('30', 256, 285);
    } else if (type === 'Z274_2_ZONE_30_ENDE') {
      // Grey square with "ZONE" and grey 30 circle with 4 black diagonal lines
      ctx.fillStyle = '#f1f5f9';
      ctx.fillRect(20, 20, 472, 472);
      ctx.lineWidth = 12;
      ctx.strokeStyle = '#64748b';
      ctx.strokeRect(20, 20, 472, 472);

      ctx.fillStyle = '#64748b';
      ctx.font = 'bold 54px Inter, Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('ZONE', 256, 95);

      ctx.fillStyle = '#94a3b8';
      ctx.beginPath();
      ctx.arc(256, 280, 140, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#f1f5f9';
      ctx.beginPath();
      ctx.arc(256, 280, 105, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#64748b';
      ctx.font = 'bold 110px Inter, Arial, sans-serif';
      ctx.textBaseline = 'middle';
      ctx.fillText('30', 256, 285);

      // 4 Black diagonal stripes across
      ctx.strokeStyle = '#0f172a';
      ctx.lineWidth = 14;
      for (let i = -45; i <= 45; i += 30) {
        ctx.beginPath();
        ctx.moveTo(120 + i, 400);
        ctx.lineTo(380 + i, 160);
        ctx.stroke();
      }
    } else if (type === 'Z306_VORFAHRTSSTRASSE') {
      // Yellow Diamond (Vorfahrtsstraße)
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.moveTo(256, 32);
      ctx.lineTo(480, 256);
      ctx.lineTo(256, 480);
      ctx.lineTo(32, 256);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = '#eab308'; // Yellow core
      ctx.beginPath();
      ctx.moveTo(256, 90);
      ctx.lineTo(422, 256);
      ctx.lineTo(256, 422);
      ctx.lineTo(90, 256);
      ctx.closePath();
      ctx.fill();
    } else if (type === 'Z307_ENDE_VORFAHRTSSTRASSE') {
      // Yellow Diamond with 3 black diagonal lines
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.moveTo(256, 32);
      ctx.lineTo(480, 256);
      ctx.lineTo(256, 480);
      ctx.lineTo(32, 256);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = '#eab308';
      ctx.beginPath();
      ctx.moveTo(256, 90);
      ctx.lineTo(422, 256);
      ctx.lineTo(256, 422);
      ctx.lineTo(90, 256);
      ctx.closePath();
      ctx.fill();

      // 3 Black diagonal stripes
      ctx.strokeStyle = '#0f172a';
      ctx.lineWidth = 18;
      for (let i = -40; i <= 40; i += 40) {
        ctx.beginPath();
        ctx.moveTo(100 + i, 420);
        ctx.lineTo(410 + i, 100);
        ctx.stroke();
      }
    } else if (type === 'Z301_VORFAHRT_KREUZUNG') {
      // Red warning triangle with thick black vertical arrow intersected by thin horizontal crossbar
      ctx.fillStyle = '#dc2626';
      ctx.beginPath();
      ctx.moveTo(256, 32);
      ctx.lineTo(480, 460);
      ctx.lineTo(32, 460);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.moveTo(256, 120);
      ctx.lineTo(416, 420);
      ctx.lineTo(96, 420);
      ctx.closePath();
      ctx.fill();

      // Thick black arrow
      ctx.fillStyle = '#0f172a';
      ctx.beginPath();
      ctx.moveTo(256, 170);
      ctx.lineTo(290, 240);
      ctx.lineTo(272, 240);
      ctx.lineTo(272, 380);
      ctx.lineTo(240, 380);
      ctx.lineTo(240, 240);
      ctx.lineTo(222, 240);
      ctx.closePath();
      ctx.fill();

      // Thin crossbar
      ctx.fillRect(200, 280, 112, 20);
    } else if (type === 'Z102_KREUZUNG_RVL') {
      // German StVO Zeichen 102: Danger triangle with equal-armed black cross (Rechts vor Links)
      ctx.fillStyle = '#dc2626';
      ctx.beginPath();
      ctx.moveTo(256, 32);
      ctx.lineTo(480, 460);
      ctx.lineTo(32, 460);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.moveTo(256, 120);
      ctx.lineTo(416, 420);
      ctx.lineTo(96, 420);
      ctx.closePath();
      ctx.fill();

      // Black symmetrical cross
      ctx.fillStyle = '#0f172a';
      // Vertical bar
      ctx.fillRect(238, 200, 36, 170);
      // Horizontal bar
      ctx.fillRect(171, 267, 170, 36);
    } else if (type === 'Z293_ZEBRASTREIFEN' || type === 'Z350_FUSSGAENGERUEBERWEG') {
      // Blue Square with white triangle and pedestrian silhouette
      ctx.fillStyle = '#1d4ed8';
      ctx.fillRect(24, 24, 464, 464);

      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.moveTo(256, 70);
      ctx.lineTo(430, 420);
      ctx.lineTo(82, 420);
      ctx.closePath();
      ctx.fill();

      // Black pedestrian & zebra stripes
      ctx.fillStyle = '#0f172a';
      // Stripes
      for (let s = 140; s <= 370; s += 50) {
        ctx.fillRect(s, 380, 32, 16);
      }
      // Head
      ctx.beginPath();
      ctx.arc(256, 180, 26, 0, Math.PI * 2);
      ctx.fill();
      // Body & Walking Legs
      ctx.lineWidth = 26;
      ctx.strokeStyle = '#0f172a';
      ctx.beginPath();
      ctx.moveTo(256, 210);
      ctx.lineTo(256, 280);
      ctx.lineTo(210, 370);
      ctx.moveTo(256, 280);
      ctx.lineTo(295, 370);
      ctx.stroke();
    } else if (type === 'Z134_FUSSGAENGER_WARNUNG') {
      // Red warning triangle with pedestrian
      ctx.fillStyle = '#dc2626';
      ctx.beginPath();
      ctx.moveTo(256, 32);
      ctx.lineTo(480, 460);
      ctx.lineTo(32, 460);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.moveTo(256, 120);
      ctx.lineTo(416, 420);
      ctx.lineTo(96, 420);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = '#0f172a';
      ctx.beginPath();
      ctx.arc(256, 210, 24, 0, Math.PI * 2);
      ctx.fill();
      ctx.lineWidth = 24;
      ctx.strokeStyle = '#0f172a';
      ctx.beginPath();
      ctx.moveTo(256, 235);
      ctx.lineTo(256, 300);
      ctx.lineTo(215, 380);
      ctx.moveTo(256, 300);
      ctx.lineTo(290, 380);
      ctx.stroke();
    } else if (type === 'Z136_ACHTUNG_KINDER') {
      // Red warning triangle with running children
      ctx.fillStyle = '#dc2626';
      ctx.beginPath();
      ctx.moveTo(256, 32);
      ctx.lineTo(480, 460);
      ctx.lineTo(32, 460);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.moveTo(256, 120);
      ctx.lineTo(416, 420);
      ctx.lineTo(96, 420);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = '#0f172a';
      // Child 1
      ctx.beginPath();
      ctx.arc(220, 210, 22, 0, Math.PI * 2);
      ctx.fill();
      // Child 2
      ctx.beginPath();
      ctx.arc(300, 250, 18, 0, Math.PI * 2);
      ctx.fill();
      ctx.lineWidth = 18;
      ctx.strokeStyle = '#0f172a';
      ctx.beginPath();
      ctx.moveTo(220, 235);
      ctx.lineTo(220, 300);
      ctx.lineTo(180, 370);
      ctx.moveTo(220, 300);
      ctx.lineTo(255, 370);
      ctx.stroke();
    } else if (type === 'Z101_GEFAHRSTELLE') {
      // Red warning triangle with exclamation mark !
      ctx.fillStyle = '#dc2626';
      ctx.beginPath();
      ctx.moveTo(256, 32);
      ctx.lineTo(480, 460);
      ctx.lineTo(32, 460);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.moveTo(256, 120);
      ctx.lineTo(416, 420);
      ctx.lineTo(96, 420);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = '#0f172a';
      ctx.fillRect(240, 190, 32, 130);
      ctx.beginPath();
      ctx.arc(256, 365, 18, 0, Math.PI * 2);
      ctx.fill();
    } else if (type === 'Z131_LICHTZEICHENANLAGE') {
      // Red warning triangle with traffic light symbol (Red, Yellow, Green)
      ctx.fillStyle = '#dc2626';
      ctx.beginPath();
      ctx.moveTo(256, 32);
      ctx.lineTo(480, 460);
      ctx.lineTo(32, 460);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.moveTo(256, 120);
      ctx.lineTo(416, 420);
      ctx.lineTo(96, 420);
      ctx.closePath();
      ctx.fill();

      // Traffic light box
      ctx.fillStyle = '#1e293b';
      ctx.fillRect(226, 180, 60, 180);

      // Red, Yellow, Green lamps
      ctx.fillStyle = '#ef4444';
      ctx.beginPath();
      ctx.arc(256, 210, 18, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#f59e0b';
      ctx.beginPath();
      ctx.arc(256, 270, 18, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#22c55e';
      ctx.beginPath();
      ctx.arc(256, 330, 18, 0, Math.PI * 2);
      ctx.fill();
    } else if (type === 'Z283_ABSOLUTES_HALTEVERBOT') {
      // Blue circle with red cross X and red border
      ctx.fillStyle = '#dc2626';
      ctx.beginPath();
      ctx.arc(256, 256, 230, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#1d4ed8';
      ctx.beginPath();
      ctx.arc(256, 256, 176, 0, Math.PI * 2);
      ctx.fill();

      // Red X
      ctx.strokeStyle = '#dc2626';
      ctx.lineWidth = 40;
      ctx.beginPath();
      ctx.moveTo(130, 130);
      ctx.lineTo(382, 382);
      ctx.moveTo(382, 130);
      ctx.lineTo(130, 382);
      ctx.stroke();
    } else if (type === 'Z286_EINGESCHRAENKTES_HALTEVERBOT') {
      // Blue circle with single red diagonal bar
      ctx.fillStyle = '#dc2626';
      ctx.beginPath();
      ctx.arc(256, 256, 230, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#1d4ed8';
      ctx.beginPath();
      ctx.arc(256, 256, 176, 0, Math.PI * 2);
      ctx.fill();

      // Single diagonal bar
      ctx.strokeStyle = '#dc2626';
      ctx.lineWidth = 40;
      ctx.beginPath();
      ctx.moveTo(382, 130);
      ctx.lineTo(130, 382);
      ctx.stroke();
    } else if (type === 'Z267_VERBOT_DER_EINFAHRT') {
      // Red circle with white horizontal bar
      ctx.fillStyle = '#dc2626';
      ctx.beginPath();
      ctx.arc(256, 256, 230, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(70, 220, 372, 72);
    } else if (type === 'Z222_VORBEIFAHRT_RECHTS') {
      // Blue circle with white diagonal arrow pointing down-right
      ctx.fillStyle = '#1d4ed8';
      ctx.beginPath();
      ctx.arc(256, 256, 230, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#ffffff';
      ctx.lineWidth = 36;
      ctx.strokeStyle = '#ffffff';
      ctx.beginPath();
      ctx.moveTo(170, 170);
      ctx.lineTo(310, 310);
      ctx.stroke();

      // Arrow head
      ctx.beginPath();
      ctx.moveTo(335, 335);
      ctx.lineTo(245, 340);
      ctx.lineTo(340, 245);
      ctx.closePath();
      ctx.fill();
    } else if (type === 'Z314_PARKEN') {
      // Blue Square with white P
      ctx.fillStyle = '#1d4ed8';
      ctx.fillRect(24, 24, 464, 464);

      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 280px Inter, Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('P', 256, 256);
    } else if (type === 'Z325_VERKEHRSBERUHIGT') {
      // Blue rectangle Spielstraße with house, car, ball, playing child
      ctx.fillStyle = '#1d4ed8';
      ctx.fillRect(20, 60, 472, 392);

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(50, 90, 80, 80); // House
      ctx.fillRect(220, 280, 160, 60); // Car
      ctx.beginPath();
      ctx.arc(140, 290, 18, 0, Math.PI * 2); // Ball
      ctx.fill();
    } else if (type === 'Z310_ORTSTAFEL') {
      // Yellow German town entrance plate "Musterstadt"
      ctx.fillStyle = '#facc15';
      ctx.fillRect(20, 90, 472, 332);
      ctx.lineWidth = 14;
      ctx.strokeStyle = '#0f172a';
      ctx.strokeRect(20, 90, 472, 332);

      ctx.fillStyle = '#0f172a';
      ctx.font = 'bold 52px Inter, Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Musterstadt', 256, 230);
      ctx.font = '600 32px Inter, Arial, sans-serif';
      ctx.fillText('Kreis Fahrschule', 256, 295);
    } else if (type === 'Z103_KURVE_LINKS' || type === 'Z103_KURVE_RECHTS') {
      // Red warning triangle with curved black arrow
      ctx.fillStyle = '#dc2626';
      ctx.beginPath();
      ctx.moveTo(256, 32);
      ctx.lineTo(480, 460);
      ctx.lineTo(32, 460);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.moveTo(256, 120);
      ctx.lineTo(416, 420);
      ctx.lineTo(96, 420);
      ctx.closePath();
      ctx.fill();

      // Curved black arrow
      ctx.strokeStyle = '#0f172a';
      ctx.lineWidth = 32;
      ctx.lineCap = 'round';
      ctx.beginPath();
      if (type === 'Z103_KURVE_LINKS') {
        ctx.moveTo(270, 390);
        ctx.lineTo(270, 290);
        ctx.quadraticCurveTo(270, 230, 200, 230);
        ctx.stroke();
        // Arrow head left
        ctx.fillStyle = '#0f172a';
        ctx.beginPath();
        ctx.moveTo(175, 230);
        ctx.lineTo(220, 195);
        ctx.lineTo(220, 265);
        ctx.closePath();
        ctx.fill();
      } else {
        ctx.moveTo(242, 390);
        ctx.lineTo(242, 290);
        ctx.quadraticCurveTo(242, 230, 312, 230);
        ctx.stroke();
        // Arrow head right
        ctx.fillStyle = '#0f172a';
        ctx.beginPath();
        ctx.moveTo(337, 230);
        ctx.lineTo(292, 195);
        ctx.lineTo(292, 265);
        ctx.closePath();
        ctx.fill();
      }
    } else if (type === 'Z105_DOPPELKURVE') {
      // Red warning triangle with double S-curve
      ctx.fillStyle = '#dc2626';
      ctx.beginPath();
      ctx.moveTo(256, 32);
      ctx.lineTo(480, 460);
      ctx.lineTo(32, 460);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.moveTo(256, 120);
      ctx.lineTo(416, 420);
      ctx.lineTo(96, 420);
      ctx.closePath();
      ctx.fill();

      ctx.strokeStyle = '#0f172a';
      ctx.lineWidth = 30;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(256, 390);
      ctx.lineTo(256, 330);
      ctx.lineTo(220, 290);
      ctx.lineTo(290, 230);
      ctx.lineTo(290, 185);
      ctx.stroke();

      ctx.fillStyle = '#0f172a';
      ctx.beginPath();
      ctx.moveTo(290, 160);
      ctx.lineTo(255, 205);
      ctx.lineTo(325, 205);
      ctx.closePath();
      ctx.fill();
    } else if (type === 'Z625_KURVENLEITTAFEL_LINKS' || type === 'Z625_KURVENLEITTAFEL_RECHTS') {
      // Red and white chevron curve warning panel
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(20, 80, 472, 352);
      ctx.fillStyle = '#dc2626';
      for (let i = 0; i < 4; i++) {
        ctx.beginPath();
        const startX = 40 + i * 110;
        if (type === 'Z625_KURVENLEITTAFEL_LINKS') {
          ctx.moveTo(startX + 60, 80);
          ctx.lineTo(startX, 256);
          ctx.lineTo(startX + 60, 432);
          ctx.lineTo(startX + 100, 432);
          ctx.lineTo(startX + 40, 256);
          ctx.lineTo(startX + 100, 80);
        } else {
          ctx.moveTo(startX, 80);
          ctx.lineTo(startX + 60, 256);
          ctx.lineTo(startX, 432);
          ctx.lineTo(startX + 40, 432);
          ctx.lineTo(startX + 100, 256);
          ctx.lineTo(startX + 40, 80);
        }
        ctx.closePath();
        ctx.fill();
      }
    } else if (type === 'Z276_UEBERHOLVERBOT') {
      // Red ring with red car on left, black car on right
      ctx.fillStyle = '#dc2626';
      ctx.beginPath();
      ctx.arc(256, 256, 230, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(256, 256, 176, 0, Math.PI * 2);
      ctx.fill();

      // Red car (left)
      ctx.fillStyle = '#dc2626';
      ctx.fillRect(130, 230, 90, 50);
      ctx.fillRect(150, 205, 55, 30);

      // Black car (right)
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(290, 230, 90, 50);
      ctx.fillRect(310, 205, 55, 30);
    } else if (type === 'Z224_HALTESTELLE') {
      // Yellow circle with green border and green H
      ctx.fillStyle = '#facc15';
      ctx.beginPath();
      ctx.arc(256, 256, 220, 0, Math.PI * 2);
      ctx.fill();

      ctx.strokeStyle = '#15803d';
      ctx.lineWidth = 26;
      ctx.stroke();

      ctx.fillStyle = '#15803d';
      ctx.font = 'bold 240px Inter, Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('H', 256, 256);
    } else if (type === 'Z357_SACKGASSE') {
      // Blue square with red T
      ctx.fillStyle = '#1d4ed8';
      ctx.fillRect(24, 24, 464, 464);

      // White base
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(230, 160, 52, 230);
      // Red top bar
      ctx.fillStyle = '#dc2626';
      ctx.fillRect(170, 120, 172, 48);
    } else if (type === 'Z123_BAUSTELLE') {
      // Red warning triangle with construction worker
      ctx.fillStyle = '#dc2626';
      ctx.beginPath();
      ctx.moveTo(256, 32);
      ctx.lineTo(480, 460);
      ctx.lineTo(32, 460);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.moveTo(256, 120);
      ctx.lineTo(416, 420);
      ctx.lineTo(96, 420);
      ctx.closePath();
      ctx.fill();

      // Worker silhouette
      ctx.fillStyle = '#0f172a';
      ctx.beginPath();
      ctx.arc(230, 210, 20, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillRect(210, 240, 50, 80);
      ctx.fillRect(280, 310, 80, 30);
    } else if (type === 'Z201_ANDREASKREUZ') {
      // German StVO Zeichen 201: Andreaskreuz (Dem Schienenverkehr Vorrang gewähren)
      ctx.clearRect(0, 0, 512, 512);
      const drawBeam = (angle: number) => {
        ctx.save();
        ctx.translate(256, 256);
        ctx.rotate(angle);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(-220, -32, 440, 64);
        ctx.strokeStyle = '#0f172a';
        ctx.lineWidth = 4;
        ctx.strokeRect(-220, -32, 440, 64);
        ctx.fillStyle = '#dc2626';
        ctx.fillRect(-220, -32, 65, 64);
        ctx.fillRect(155, -32, 65, 64);
        ctx.restore();
      };
      drawBeam(Math.PI / 4);
      drawBeam(-Math.PI / 4);
    } else if (type === 'Z156_BAKE_3_STREIFEN' || type === 'Z159_BAKE_2_STREIFEN' || type === 'Z162_BAKE_1_STREIFEN') {
      // German StVO Bahnübergang Baken (240m, 160m, 80m)
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(80, 20, 352, 472);
      ctx.lineWidth = 8;
      ctx.strokeStyle = '#0f172a';
      ctx.strokeRect(80, 20, 352, 472);

      const stripes = type === 'Z156_BAKE_3_STREIFEN' ? 3 : type === 'Z159_BAKE_2_STREIFEN' ? 2 : 1;
      ctx.fillStyle = '#dc2626';
      const startY = stripes === 3 ? [80, 210, 340] : stripes === 2 ? [140, 280] : [220];
      for (const y of startY) {
        ctx.beginPath();
        ctx.moveTo(80, y);
        ctx.lineTo(432, y - 60);
        ctx.lineTo(432, y - 60 + 55);
        ctx.lineTo(80, y + 55);
        ctx.closePath();
        ctx.fill();
      }
    } else if (type === 'Z237_RADWEG') {
      // Blue circle with white bicycle
      ctx.fillStyle = '#1d4ed8';
      ctx.beginPath();
      ctx.arc(256, 256, 230, 0, Math.PI * 2);
      ctx.fill();

      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 14;
      ctx.beginPath();
      ctx.arc(150, 310, 48, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(362, 310, 48, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(150, 310);
      ctx.lineTo(240, 310);
      ctx.lineTo(310, 230);
      ctx.lineTo(210, 230);
      ctx.closePath();
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(362, 310);
      ctx.lineTo(320, 180);
      ctx.lineTo(345, 175);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(240, 310);
      ctx.lineTo(210, 200);
      ctx.moveTo(190, 200);
      ctx.lineTo(230, 200);
      ctx.stroke();
    } else if (type === 'Z138_RADVERKEHR') {
      // Red warning triangle with bicycle
      ctx.fillStyle = '#dc2626';
      ctx.beginPath();
      ctx.moveTo(256, 32);
      ctx.lineTo(480, 460);
      ctx.lineTo(32, 460);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.moveTo(256, 120);
      ctx.lineTo(416, 420);
      ctx.lineTo(96, 420);
      ctx.closePath();
      ctx.fill();

      ctx.strokeStyle = '#0f172a';
      ctx.lineWidth = 10;
      ctx.beginPath();
      ctx.arc(180, 340, 32, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(330, 340, 32, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(180, 340);
      ctx.lineTo(240, 340);
      ctx.lineTo(290, 280);
      ctx.lineTo(220, 280);
      ctx.closePath();
      ctx.stroke();
    } else if (
      type === 'STRASSE_HAUPTSTRASSE' ||
      type === 'STRASSE_GOETHESTRASSE' ||
      type === 'STRASSE_BAHNHOFSTRASSE' ||
      type === 'STRASSE_SCHULSTRASSE'
    ) {
      // German Dark Blue Street Name Sign with white border & text
      ctx.fillStyle = '#1e3a8a';
      ctx.fillRect(16, 140, 480, 232);
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 10;
      ctx.strokeRect(26, 150, 460, 212);

      const nameMap: Record<string, string> = {
        STRASSE_HAUPTSTRASSE: 'Hauptstraße',
        STRASSE_GOETHESTRASSE: 'Goethestraße',
        STRASSE_BAHNHOFSTRASSE: 'Bahnhofstraße',
        STRASSE_SCHULSTRASSE: 'Schulstraße',
      };
      const streetName = nameMap[type] || 'Hauptstraße';

      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 50px Inter, Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(streetName, 256, 256);
    } else if (type === 'Z220_EINBAHNSTRASSE') {
      ctx.fillStyle = '#1d4ed8';
      ctx.fillRect(16, 140, 480, 232);
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 10;
      ctx.strokeRect(26, 150, 460, 212);
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 42px Inter, Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('EINBAHNSTRASSE ➔', 256, 256);
    } else if (type === 'Z244_FAHRRADSTRASSE') {
      ctx.fillStyle = '#1d4ed8';
      ctx.beginPath();
      ctx.arc(256, 256, 220, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 14;
      ctx.stroke();

      // Bike icon
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 12;
      ctx.beginPath();
      ctx.arc(160, 270, 45, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(352, 270, 45, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(160, 270);
      ctx.lineTo(240, 270);
      ctx.lineTo(310, 200);
      ctx.lineTo(220, 200);
      ctx.closePath();
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(352, 270);
      ctx.lineTo(320, 170);
      ctx.stroke();

      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 36px Inter, Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Fahrradstraße', 256, 370);
    } else if (type === 'Z245_ENDE_FAHRRADSTRASSE') {
      ctx.fillStyle = '#64748b';
      ctx.beginPath();
      ctx.arc(256, 256, 220, 0, Math.PI * 2);
      ctx.fill();

      // Bike icon
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 10;
      ctx.beginPath();
      ctx.arc(160, 270, 45, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(352, 270, 45, 0, Math.PI * 2);
      ctx.stroke();

      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 36px Inter, Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Fahrradstraße', 256, 370);

      // Red strike-through
      ctx.strokeStyle = '#dc2626';
      ctx.lineWidth = 14;
      for (let i = -2; i <= 2; i++) {
        ctx.beginPath();
        ctx.moveTo(120 + i * 25, 420);
        ctx.lineTo(392 + i * 25, 92);
        ctx.stroke();
      }
    } else if (type === 'Z330_1_AUTOBAHN_BEGINN') {
      ctx.fillStyle = '#1d4ed8';
      ctx.fillRect(20, 20, 472, 472);
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 14;
      ctx.strokeRect(28, 28, 456, 456);

      // White carriageways in perspective
      ctx.fillStyle = '#ffffff';
      // Left carriageway
      ctx.beginPath();
      ctx.moveTo(70, 440);
      ctx.lineTo(170, 440);
      ctx.lineTo(210, 230);
      ctx.lineTo(170, 230);
      ctx.closePath();
      ctx.fill();
      // Right carriageway
      ctx.beginPath();
      ctx.moveTo(342, 440);
      ctx.lineTo(442, 440);
      ctx.lineTo(342, 230);
      ctx.lineTo(302, 230);
      ctx.closePath();
      ctx.fill();
      // Overpass bridge
      ctx.fillRect(110, 210, 292, 35);
    } else if (type === 'Z330_2_AUTOBAHN_ENDE') {
      ctx.fillStyle = '#1d4ed8';
      ctx.fillRect(20, 20, 472, 472);
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 14;
      ctx.strokeRect(28, 28, 456, 456);

      // White carriageways
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.moveTo(70, 440);
      ctx.lineTo(170, 440);
      ctx.lineTo(210, 230);
      ctx.lineTo(170, 230);
      ctx.closePath();
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(342, 440);
      ctx.lineTo(442, 440);
      ctx.lineTo(342, 230);
      ctx.lineTo(302, 230);
      ctx.closePath();
      ctx.fill();
      ctx.fillRect(110, 210, 292, 35);

      // 5 Red diagonal stripes
      ctx.strokeStyle = '#dc2626';
      ctx.lineWidth = 16;
      for (let i = -2; i <= 2; i++) {
        ctx.beginPath();
        ctx.moveTo(80 + i * 40, 450);
        ctx.lineTo(420 + i * 40, 50);
        ctx.stroke();
      }
    } else if (type === 'Z311_ORTSTAFEL_ENDE') {
      ctx.fillStyle = '#facc15';
      ctx.fillRect(16, 80, 480, 352);
      ctx.strokeStyle = '#0f172a';
      ctx.lineWidth = 12;
      ctx.strokeRect(24, 88, 464, 336);

      ctx.fillStyle = '#0f172a';
      ctx.font = 'bold 56px Inter, Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Musterstadt', 256, 200);

      // Red strike-through across city name
      ctx.strokeStyle = '#dc2626';
      ctx.lineWidth = 16;
      ctx.beginPath();
      ctx.moveTo(60, 240);
      ctx.lineTo(452, 160);
      ctx.stroke();

      ctx.strokeStyle = '#0f172a';
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.moveTo(40, 270);
      ctx.lineTo(472, 270);
      ctx.stroke();

      ctx.font = 'bold 36px Inter, Arial, sans-serif';
      ctx.fillText('Landkreis 4 km', 256, 350);
    } else if (type === 'Z282_AUFHEBUNG') {
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(256, 256, 220, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#94a3b8';
      ctx.lineWidth = 10;
      ctx.stroke();

      ctx.strokeStyle = '#0f172a';
      ctx.lineWidth = 10;
      for (let i = -2; i <= 2; i++) {
        ctx.beginPath();
        ctx.moveTo(110 + i * 22, 400);
        ctx.lineTo(402 + i * 22, 112);
        ctx.stroke();
      }
    } else if (type === 'Z450_BAKE_300M' || type === 'Z450_BAKE_200M' || type === 'Z450_BAKE_100M') {
      ctx.fillStyle = '#1d4ed8';
      ctx.fillRect(80, 20, 352, 472);
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 12;
      ctx.strokeRect(88, 28, 336, 456);

      const numStripes = type === 'Z450_BAKE_300M' ? 3 : type === 'Z450_BAKE_200M' ? 2 : 1;
      const text = type === 'Z450_BAKE_300M' ? '300 m' : type === 'Z450_BAKE_200M' ? '200 m' : '100 m';

      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 48px Inter, Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(text, 256, 100);

      // Diagonal white stripes (angled up to the right)
      ctx.fillStyle = '#ffffff';
      for (let i = 0; i < numStripes; i++) {
        const yBase = 180 + i * 90;
        ctx.beginPath();
        ctx.moveTo(100, yBase + 45);
        ctx.lineTo(412, yBase - 35);
        ctx.lineTo(412, yBase);
        ctx.lineTo(100, yBase + 80);
        ctx.closePath();
        ctx.fill();
      }
    } else if (type === 'Z315_PARKEN_GEHWEG') {
      ctx.fillStyle = '#1d4ed8';
      ctx.fillRect(20, 20, 472, 472);
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 14;
      ctx.strokeRect(28, 28, 456, 456);

      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 150px Inter, Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('P', 256, 210);

      // Sidewalk & car graphic
      ctx.fillRect(100, 360, 312, 16);
      ctx.fillRect(100, 330, 160, 30);
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.anisotropy = 8;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.generateMipmaps = true;

    const signMat = new THREE.MeshStandardMaterial({
      map: texture,
      roughness: 0.25,
      metalness: 0.1,
    });

    const aluMat = new THREE.MeshStandardMaterial({
      color: 0x64748b, // Brushed galvanized aluminum
      metalness: 0.75,
      roughness: 0.45,
    });

    // 1. Solid Aluminum Backing Box with physical depth
    const aluBox = new THREE.Mesh(new THREE.BoxGeometry(width, height, depth), aluMat);
    aluBox.castShadow = true;
    plateGroup.add(aluBox);

    // 2. Front Face with texture
    const frontMesh = new THREE.Mesh(new THREE.PlaneGeometry(width, height), signMat);
    frontMesh.position.z = depth / 2 + 0.001;
    plateGroup.add(frontMesh);

    // 3. Galvanized mounting bracket clamp
    const clampMat = new THREE.MeshStandardMaterial({ color: 0x334155, metalness: 0.8, roughness: 0.3 });
    const clamp = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.05, 0.06), clampMat);
    clamp.position.set(0, 0, -depth / 2 - 0.02);
    plateGroup.add(clamp);

    // 4. Double-sided support for street signs and town entrance signs
    if (isStreetSign || isOrtstafel) {
      const backMesh = new THREE.Mesh(new THREE.PlaneGeometry(width, height), signMat);
      backMesh.rotation.y = Math.PI;
      backMesh.position.z = -depth / 2 - 0.001;
      plateGroup.add(backMesh);

      // Add perpendicular cross-arm for intersection street name signs
      if (type === 'STRASSE_SCHULSTRASSE') {
        const crossArm = TrafficSigns.createStreetNameArm('Goethestraße', width, height, depth, aluMat);
        crossArm.position.y = 0.28;
        crossArm.rotation.y = Math.PI / 2;
        plateGroup.add(crossArm);
      } else if (type === 'STRASSE_GOETHESTRASSE') {
        const crossArm = TrafficSigns.createStreetNameArm('Hauptstraße', width, height, depth, aluMat);
        crossArm.position.y = 0.28;
        crossArm.rotation.y = Math.PI / 2;
        plateGroup.add(crossArm);
      }
    }

    return plateGroup;
  }

  private static createStreetNameArm(
    streetName: string,
    width: number,
    height: number,
    depth: number,
    aluMat: THREE.Material
  ): THREE.Group {
    const armGroup = new THREE.Group();
    if (typeof document === 'undefined') return armGroup;

    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');
    if (!ctx) return armGroup;

    ctx.fillStyle = '#1e3a8a';
    ctx.fillRect(16, 140, 480, 232);
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 10;
    ctx.strokeRect(26, 150, 460, 212);

    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 50px Inter, Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(streetName, 256, 256);

    const texture = new THREE.CanvasTexture(canvas);
    texture.anisotropy = 8;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.generateMipmaps = true;

    const signMat = new THREE.MeshStandardMaterial({
      map: texture,
      roughness: 0.25,
      metalness: 0.1,
    });

    const box = new THREE.Mesh(new THREE.BoxGeometry(width, height, depth), aluMat);
    armGroup.add(box);

    const front = new THREE.Mesh(new THREE.PlaneGeometry(width, height), signMat);
    front.position.z = depth / 2 + 0.001;
    armGroup.add(front);

    const back = new THREE.Mesh(new THREE.PlaneGeometry(width, height), signMat);
    back.rotation.y = Math.PI;
    back.position.z = -depth / 2 - 0.001;
    armGroup.add(back);

    return armGroup;
  }

  public static createKreisverkehrSignPost(position: THREE.Vector3, rotationY: number = 0): THREE.Group {
    const group = new THREE.Group();
    group.name = 'kreisverkehr_signpost';
    group.position.copy(position);
    group.rotation.y = rotationY;

    // German Galvanized Steel Pole (2.8m height)
    const poleGeom = new THREE.CylinderGeometry(0.035, 0.035, 3.0, 16);
    const poleMat = new THREE.MeshStandardMaterial({ color: 0x94a3b8, metalness: 0.8, roughness: 0.3 });
    const pole = new THREE.Mesh(poleGeom, poleMat);
    pole.position.y = 1.5;
    pole.castShadow = true;
    group.add(pole);

    // Top: Zeichen 205 (Vorfahrt gewähren) at y = 2.45
    const z205Plate = TrafficSigns.createSignPlate('Z205_VORFAHRT_GEWAEHREN');
    z205Plate.position.set(0, 2.45, 0.04);
    group.add(z205Plate);

    // Bottom: Zeichen 215 (Kreisverkehr with 3 curved white arrows) at y = 1.65
    const z215Plate = TrafficSigns.createSignPlate('Z215_KREISVERKEHR');
    z215Plate.position.set(0, 1.65, 0.04);
    group.add(z215Plate);

    return group;
  }

  public static createAndreaskreuzPost(position: THREE.Vector3, rotationY: number = 0, withBlinklicht: boolean = true): THREE.Group {
    const group = new THREE.Group();
    group.name = 'andreaskreuz_post';
    group.position.copy(position);
    group.rotation.y = rotationY;

    const poleGeom = new THREE.CylinderGeometry(0.04, 0.04, 3.0, 16);
    const poleMat = new THREE.MeshStandardMaterial({ color: 0x94a3b8, metalness: 0.8, roughness: 0.3 });
    const pole = new THREE.Mesh(poleGeom, poleMat);
    pole.position.y = 1.5;
    pole.castShadow = true;
    group.add(pole);

    // Andreaskreuz plate at y = 2.15
    const akPlate = TrafficSigns.createSignPlate('Z201_ANDREASKREUZ');
    akPlate.position.set(0, 2.15, 0.04);
    group.add(akPlate);

    // Red flashing light head (Blinklicht) at y = 2.75
    if (withBlinklicht) {
      const lampHousing = new THREE.Mesh(
        new THREE.BoxGeometry(0.24, 0.24, 0.18),
        new THREE.MeshStandardMaterial({ color: 0x1e293b, roughness: 0.7 })
      );
      lampHousing.position.set(0, 2.75, 0.04);
      const redLens = new THREE.Mesh(
        new THREE.CylinderGeometry(0.08, 0.08, 0.04, 16),
        new THREE.MeshBasicMaterial({ color: 0xef4444 })
      );
      redLens.rotation.x = Math.PI / 2;
      redLens.position.set(0, 2.75, 0.14);
      group.add(lampHousing, redLens);
    }

    return group;
  }
}

